import type {MutableRefObject} from 'react';
import {addToHistoryStack} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {store} from 'store/customer/storeSetup';
import type {HistoryActionContext} from 'components/customer/RoomPlanner/helpers/history/historyActionContext';
import type {
    CabinetMesh,
    HistoryStack,
} from 'components/customer/RoomPlanner/types';
import {
    applyPlaceholderDimensions,
    loadPlaceholderGLB,
    configFromPlaceholderMesh,
    syncPlaceholderCabinetDimensions,
    syncPlaceholderProductEvents,
    PlaceholderConfig,
    PlaceholderDimensionsMm,
    PlaceholderUserData,
} from 'components/customer/RoomPlanner/helpers/placeholder/loadPlaceholderGLB';
import {removePlaceholderFromScene} from 'components/customer/RoomPlanner/helpers/placeholder/removePlaceholder';
import {
    findPlaceholderGroup,
    findPlaceholderGroupOf,
    getPlaceholderGroupId,
    syncPlaceholderQuantitiesFromGroups,
} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';

export type PlaceholderHistorySnapshot = {
    clientId: string;
    name: string;
    config: PlaceholderConfig;
    position: {x: number; y: number; z: number};
    rotation: {x: number; y: number; z: number};
    currentDirection: string;
};

export const snapshotPlaceholder = (
    group: CabinetMesh
): PlaceholderHistorySnapshot | null => {
    const config = configFromPlaceholderMesh(group);
    if (!config) return null;
    const userData = group.userData as PlaceholderUserData;
    return {
        clientId: String(userData.id ?? ''),
        name: userData.placeholderName || config.name || 'Placeholder',
        config,
        position: {
            x: group.position.x,
            y: group.position.y,
            z: group.position.z,
        },
        rotation: {
            x: group.rotation.x,
            y: group.rotation.y,
            z: group.rotation.z,
        },
        currentDirection: userData.currentDirection || 'front',
    };
};

const restorePlaceholder = async (
    snapshot: PlaceholderHistorySnapshot,
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>,
    options: {sync?: boolean} = {}
): Promise<void> => {
    const scene = CameraSetup?.scene;
    if (!scene || !snapshot.clientId) return;
    const exists = placeholderListRef.current.some(
        (item) => String(item.userData?.id) === snapshot.clientId
    );
    if (exists) return;

    const group = await loadPlaceholderGLB(
        {
            ...snapshot.config,
            id: snapshot.clientId,
            initialX: snapshot.position.x,
            initialZ: snapshot.position.z,
        },
        scene
    );

    const dims = snapshot.config;
    if (
        dims.targetWidthMm != null &&
        dims.targetHeightMm != null &&
        dims.targetDepthMm != null
    ) {
        applyPlaceholderDimensions(group, {
            width: dims.targetWidthMm,
            height: dims.targetHeightMm,
            depth: dims.targetDepthMm,
        });
    }

    group.position.set(
        snapshot.position.x,
        snapshot.position.y,
        snapshot.position.z
    );
    group.rotation.set(
        snapshot.rotation.x,
        snapshot.rotation.y,
        snapshot.rotation.z
    );
    group.userData.currentDirection = snapshot.currentDirection;
    group.userData.savedPosition = group.position.clone();
    group.updateMatrixWorld(true);

    placeholderListRef.current.push(group);
    autoSave(group, true);
    if (options.sync !== false) {
        syncPlaceholderQuantitiesFromGroups(placeholderListRef.current);
        syncPlaceholderProductEvents(cabinetGroupListRef);
    }
};

const restorePlaceholderSnapshots = async (
    snapshots: PlaceholderHistorySnapshot[],
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>
): Promise<void> => {
    for (let index = 0; index < snapshots.length; index++) {
        const snapshot = snapshots[Number(index)];
        if (!snapshot) continue;
        await restorePlaceholder(
            snapshot,
            placeholderListRef,
            cabinetGroupListRef,
            {sync: index === snapshots.length - 1}
        );
    }
    syncPlaceholderQuantitiesFromGroups(placeholderListRef.current);
    syncPlaceholderProductEvents(cabinetGroupListRef);
};

const findPlaceholder = (
    ctx: HistoryActionContext,
    clientId: string
): CabinetMesh | undefined =>
    ctx.placeholderListRef?.current?.find(
        (item) => String(item.userData?.id) === clientId
    );

const historyCopyIds = (historyItem: HistoryStack): string[] => {
    const copies = historyItem.data?.placeholderCopyIds;
    if (copies && copies.length > 0) {
        return copies;
    }
    const id = String(historyItem.data?.id ?? '');
    return id ? [id] : [];
};

const removePlaceholdersByIds = (
    ids: string[],
    ctx: HistoryActionContext
): void => {
    if (!ctx.placeholderListRef?.current || !ctx.cabinetGroupListRef?.current) {
        return;
    }
    ids.forEach((clientId) => {
        const group = findPlaceholder(ctx, clientId);
        if (!group) return;
        removePlaceholderFromScene(
            group,
            ctx.placeholderListRef,
            ctx.cabinetGroupListRef,
            true
        );
    });
    syncPlaceholderQuantitiesFromGroups(ctx.placeholderListRef.current);
    ctx.invalidateCaches();
};

/**
 * Push an add/duplicate history entry. Redo reloads the same client ids + poses.
 * Pass every visual copy so quantity-N adds undo as one product.
 *
 * @param {CabinetMesh | CabinetMesh[]} groupOrGroups Added placeholder(s)
 * @param {MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @param {'Add' | 'Duplicate'} verb History title verb
 * @return {void}
 */
export const recordPlaceholderAdd = (
    groupOrGroups: CabinetMesh | CabinetMesh[],
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>,
    verb: 'Add' | 'Duplicate' = 'Add'
): void => {
    const groups = Array.isArray(groupOrGroups)
        ? groupOrGroups
        : [groupOrGroups];
    const snapshots = groups
        .map((item) => snapshotPlaceholder(item))
        .filter((item): item is PlaceholderHistorySnapshot => item != null);
    if (snapshots.length === 0) return;
    const first = snapshots[0];
    store.dispatch(
        addToHistoryStack({
            action: 'CABINET_ADD',
            isActive: true,
            timestamp: Date.now(),
            title: `**${verb}** ${first.name}`,
            data: {
                id: first.clientId,
                isPlaceholder: true,
                placeholderCopyIds: snapshots.map((item) => item.clientId),
                redoAction: () =>
                    restorePlaceholderSnapshots(
                        snapshots,
                        placeholderListRef,
                        cabinetGroupListRef
                    ),
            },
        })
    );
};

/**
 * Push a delete history entry. Undo restores the snapshot(s).
 *
 * @param {CabinetMesh | CabinetMesh[]} groupOrGroups Placeholder(s) about to be removed
 * @param {MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @return {void}
 */
export const recordPlaceholderDelete = (
    groupOrGroups: CabinetMesh | CabinetMesh[],
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>
): void => {
    const groups = Array.isArray(groupOrGroups)
        ? groupOrGroups
        : [groupOrGroups];
    const snapshots = groups
        .map((item) => snapshotPlaceholder(item))
        .filter((item): item is PlaceholderHistorySnapshot => item != null);
    if (snapshots.length === 0) return;
    const first = snapshots[0];
    store.dispatch(
        addToHistoryStack({
            action: 'CABINET_DELETE',
            isActive: true,
            timestamp: Date.now(),
            title: `**Delete** ${first.name}`,
            data: {
                id: first.clientId,
                isPlaceholder: true,
                placeholderCopyIds: snapshots.map((item) => item.clientId),
                undoAction: () =>
                    restorePlaceholderSnapshots(
                        snapshots,
                        placeholderListRef,
                        cabinetGroupListRef
                    ),
            },
        })
    );
};

type DeletePlaceholderOptions = {
    /** Remove every visual copy of this product (cabinet "Delete all"). */
    all?: boolean;
};

/**
 * Record history then remove one copy or the whole product.
 *
 * @param {CabinetMesh} group Placeholder to delete
 * @param {MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @param {DeletePlaceholderOptions=} options `{all: true}` removes the product
 * @return {void}
 */
export const deletePlaceholderWithHistory = (
    group: CabinetMesh,
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>,
    options: DeletePlaceholderOptions = {}
): void => {
    const targets = options.all
        ? findPlaceholderGroupOf(placeholderListRef.current, group)
        : [group];
    const groupId = getPlaceholderGroupId(group);
    recordPlaceholderDelete(targets, placeholderListRef, cabinetGroupListRef);
    targets.forEach((item) => {
        removePlaceholderFromScene(
            item,
            placeholderListRef,
            cabinetGroupListRef,
            true
        );
    });
    if (!options.all) {
        const remaining = findPlaceholderGroup(
            placeholderListRef.current,
            groupId
        );
        syncPlaceholderQuantitiesFromGroups(placeholderListRef.current);
        remaining.forEach((item) => autoSave(item, true));
    }
};

const sameNumber = (left: number, right: number): boolean =>
    Math.abs(left - right) < 1e-3;

const sameSnapshot = (
    left: PlaceholderHistorySnapshot,
    right: PlaceholderHistorySnapshot
): boolean =>
    left.clientId === right.clientId &&
    left.config.targetWidthMm === right.config.targetWidthMm &&
    left.config.targetHeightMm === right.config.targetHeightMm &&
    left.config.targetDepthMm === right.config.targetDepthMm &&
    left.currentDirection === right.currentDirection &&
    sameNumber(left.position.x, right.position.x) &&
    sameNumber(left.position.y, right.position.y) &&
    sameNumber(left.position.z, right.position.z) &&
    sameNumber(left.rotation.x, right.rotation.x) &&
    sameNumber(left.rotation.y, right.rotation.y) &&
    sameNumber(left.rotation.z, right.rotation.z);

const orderedSnapshots = (
    snapshots: PlaceholderHistorySnapshot[]
): PlaceholderHistorySnapshot[] =>
    [...snapshots].sort((left, right) =>
        left.clientId.localeCompare(right.clientId)
    );

/**
 * True when a size or quantity edit changed the product.
 * Quantity is the set of copy ids; size is the stored millimetre targets.
 *
 * @param {PlaceholderHistorySnapshot[]} before Snapshots from when editing started
 * @param {PlaceholderHistorySnapshot[]} after Snapshots after the save
 * @return {boolean} Whether undo should record the edit
 */
export const placeholderSnapshotsDiffer = (
    before: PlaceholderHistorySnapshot[],
    after: PlaceholderHistorySnapshot[]
): boolean => {
    if (before.length !== after.length) return true;
    const left = orderedSnapshots(before);
    const right = orderedSnapshots(after);
    return left.some(
        (item, index) => !sameSnapshot(item, right[Number(index)])
    );
};

export type PlaceholderEditBaseline = {
    dimensions: PlaceholderDimensionsMm;
    quantity: number;
};

const snapshotDimensions = (
    snapshot: PlaceholderHistorySnapshot
): PlaceholderDimensionsMm | null => {
    const {targetWidthMm, targetHeightMm, targetDepthMm} = snapshot.config;
    return targetWidthMm != null &&
        targetHeightMm != null &&
        targetDepthMm != null
        ? {width: targetWidthMm, height: targetHeightMm, depth: targetDepthMm}
        : null;
};

const applySnapshotPose = (
    group: CabinetMesh,
    snapshot: PlaceholderHistorySnapshot,
    fallback?: PlaceholderEditBaseline | null
): void => {
    const dimensions = snapshotDimensions(snapshot) ?? fallback?.dimensions;
    if (dimensions) {
        applyPlaceholderDimensions(group, dimensions);
        syncPlaceholderCabinetDimensions(group, dimensions, {
            quantity: fallback?.quantity,
        });
    }
    group.position.set(
        snapshot.position.x,
        snapshot.position.y,
        snapshot.position.z
    );
    group.rotation.set(
        snapshot.rotation.x,
        snapshot.rotation.y,
        snapshot.rotation.z
    );
    group.userData.currentDirection = snapshot.currentDirection;
    group.userData.savedPosition = group.position.clone();
    group.updateMatrixWorld(true);
};

/**
 * Discard a live size preview: put every copy back on the snapshot taken
 * when the editor opened. The baseline covers meshes whose config never
 * stored a size, and the edited mesh itself when no snapshot could be taken.
 *
 * @param {PlaceholderHistorySnapshot[]} snapshots Copies as the editor opened them
 * @param {CabinetMesh[]} meshes Live placeholder meshes
 * @param {PlaceholderEditBaseline | null} fallback Size and quantity from open
 * @param {CabinetMesh | null} group Mesh the form was editing
 * @return {void}
 */
export const revertPlaceholderEditPreview = (
    snapshots: PlaceholderHistorySnapshot[],
    meshes: CabinetMesh[],
    fallback: PlaceholderEditBaseline | null,
    group: CabinetMesh | null
): void => {
    let restored = false;
    snapshots.forEach((snapshot) => {
        const mesh = meshes.find(
            (item) => String(item.userData?.id ?? '') === snapshot.clientId
        );
        if (!mesh) return;

        applySnapshotPose(mesh, snapshot, fallback);
        restored = true;
    });

    if (!restored && group && fallback) {
        applyPlaceholderDimensions(group, fallback.dimensions);
        syncPlaceholderCabinetDimensions(group, fallback.dimensions, {
            quantity: fallback.quantity,
        });
    }
};

/**
 * Make the live copies match a snapshot set: drop extras, resize keepers,
 * and reload any copy the edit removed.
 *
 * @param {PlaceholderHistorySnapshot[]} snapshots Target copies
 * @param {MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @return {Promise<void>}
 */
const reconcilePlaceholderSnapshots = async (
    snapshots: PlaceholderHistorySnapshot[],
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>
): Promise<void> => {
    if (snapshots.length === 0 || !placeholderListRef.current) return;
    const groupId = getPlaceholderGroupId(snapshots[0].clientId);
    const wanted = new Set(snapshots.map((item) => item.clientId));
    const extras = placeholderListRef.current.filter((item) => {
        if (getPlaceholderGroupId(item) !== groupId) return false;
        return !wanted.has(String(item.userData?.id ?? ''));
    });
    extras.forEach((item) => {
        removePlaceholderFromScene(
            item,
            placeholderListRef,
            cabinetGroupListRef,
            true
        );
    });

    for (let index = 0; index < snapshots.length; index++) {
        const snapshot = snapshots[Number(index)];
        if (!snapshot) continue;
        const existing = placeholderListRef.current.find(
            (item) => String(item.userData?.id) === snapshot.clientId
        );
        if (existing) {
            applySnapshotPose(existing, snapshot);
            continue;
        }
        await restorePlaceholder(
            snapshot,
            placeholderListRef,
            cabinetGroupListRef,
            {sync: false}
        );
    }

    syncPlaceholderQuantitiesFromGroups(placeholderListRef.current);
    findPlaceholderGroup(placeholderListRef.current, groupId).forEach((item) =>
        autoSave(item, true)
    );
    syncPlaceholderProductEvents(cabinetGroupListRef);
};

/**
 * Record a size or quantity edit. Undo restores the copies from before the
 * save, including ones a higher quantity added, and redo applies the saved set.
 *
 * @param {PlaceholderHistorySnapshot[]} before Copies when the editor opened
 * @param {CabinetMesh[]} afterGroups Copies after the save
 * @param {MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @return {void}
 */
export const recordPlaceholderUpdate = (
    before: PlaceholderHistorySnapshot[],
    afterGroups: CabinetMesh[],
    placeholderListRef: MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>
): void => {
    if (before.length === 0) return;
    const after = afterGroups
        .map((item) => snapshotPlaceholder(item))
        .filter((item): item is PlaceholderHistorySnapshot => item != null);
    if (after.length === 0 || !placeholderSnapshotsDiffer(before, after)) {
        return;
    }
    const name = after[0]?.name || before[0]?.name || 'Placeholder';
    const clientId = after[0]?.clientId || before[0]?.clientId || '';
    store.dispatch(
        addToHistoryStack({
            action: 'CABINET_UPDATE',
            isActive: true,
            timestamp: Date.now(),
            title: `**Update** ${name}`,
            data: {
                id: clientId,
                isPlaceholder: true,
                placeholderCopyIds: after.map((item) => item.clientId),
                undoAction: () =>
                    reconcilePlaceholderSnapshots(
                        before,
                        placeholderListRef,
                        cabinetGroupListRef
                    ),
                redoAction: () =>
                    reconcilePlaceholderSnapshots(
                        after,
                        placeholderListRef,
                        cabinetGroupListRef
                    ),
            },
        })
    );
};

export const undoPlaceholderUpdate = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    if (historyItem.data?.undoAction) {
        await historyItem.data.undoAction();
    }
    ctx.invalidateCaches();
    ctx.setPreviousHistoryIndex(targetIndex);
};

export const redoPlaceholderUpdate = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    if (historyItem.data?.redoAction) {
        await historyItem.data.redoAction();
    }
    ctx.invalidateCaches();
    await ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
};

export const undoPlaceholderAdd = (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): void => {
    removePlaceholdersByIds(historyCopyIds(historyItem), ctx);
    ctx.setPreviousHistoryIndex(targetIndex);
};

export const redoPlaceholderAdd = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    if (historyItem.data?.redoAction) {
        await historyItem.data.redoAction();
    }
    await ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
};

export const undoPlaceholderDelete = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    if (historyItem.data?.undoAction) {
        await historyItem.data.undoAction();
    }
    ctx.setPreviousHistoryIndex(targetIndex);
};

export const redoPlaceholderDelete = (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): void => {
    removePlaceholdersByIds(historyCopyIds(historyItem), ctx);
    void ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
};
